import React from 'react';
import { Helmet } from 'react-helmet';
import { seoConfig, generateDefaultStructuredData, generatePerformanceMeta, generatePreconnectLinks } from '../utils/seoConfig';

const SEO = ({
  title,
  description,
  keywords = '',
  image = `${seoConfig.company.url}/assets/og-image.jpg`,
  url = seoConfig.company.url,
  type = 'website',
  structuredData = null,
  noindex = false
}) => {
  const fullTitle = title ? `${title} | ${seoConfig.company.name}` : `${seoConfig.company.name} - Móveis Únicos em Madeiras Nobres`;
  const fullDescription = description || seoConfig.company.description;
  const fullKeywords = keywords || seoConfig.keywords.primary.join(', ');

  const defaultStructuredData = generateDefaultStructuredData('LocalBusiness', {
    description: fullDescription,
    url: url
  });

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <meta name="keywords" content={fullKeywords} />
      <meta name="author" content={seoConfig.company.name} />
      
      {/* Performance Meta Tags */}
      {generatePerformanceMeta().map((meta, index) => (
        <meta key={index} name={meta.name} content={meta.content} />
      ))}
      
      {/* Robots */}
      <meta 
        name="robots" 
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} 
      />
      <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content={seoConfig.openGraph.imageWidth} />
      <meta property="og:image:height" content={seoConfig.openGraph.imageHeight} />
      <meta property="og:site_name" content={seoConfig.openGraph.siteName} />
      <meta property="og:locale" content={seoConfig.openGraph.locale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content={seoConfig.twitter.card} />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={image} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Preconnect Links */}
      {generatePreconnectLinks().map((link, index) => (
        <link key={index} rel={link.rel} href={link.href} crossOrigin={link.crossorigin} />
      ))}
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Default Structured Data if none provided */}
      {!structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(defaultStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
